import type { WorkerProfile } from './worker-candidates';

/**
 * Peran Workforce — MURNI.
 *
 * ADA KARENA lubang yang ditemukan saat menyiapkan onboarding pilot: `TenantContextGuard` menolak
 * SETIAP permintaan dari orang yang tidak punya satu pun baris `role_assignments`
 * (`NoWorkforceRoleError`). Sampai sekarang, satu-satunya cara memberi peran adalah `--admin` pada
 * skrip provisioning — yang HANYA memberi `WORKFORCE_ADMIN` — atau memanggil
 * `POST /api/roles/assignments` dengan tangan.
 *
 * Akibatnya: menambahkan seseorang lewat layar Kelola Karyawan membuatnya muncul di roster, tetapi
 * ia tetap TIDAK DAPAT MASUK. Profil pekerja dan peran adalah dua hal, dan sampai PR ini hanya
 * salah satunya punya layar.
 */

/** Sama persis dengan enum database `workforce_role` dan katalog izin di API. */
export type WorkforceRole = 'WORKFORCE_ADMIN' | 'MANAGER' | 'DISPATCHER' | 'EMPLOYEE' | 'COURIER';

/** Satu baris `workforce_role_assignments`, sebagaimana dikirim `GET /api/roles/assignments`. */
export interface RoleAssignment {
  readonly userId: string;
  readonly role: WorkforceRole;
}

/**
 * Peran yang TIDAK LAGI DITAWARKAN. Cerminan `RETIRED_ROLES` di katalog izin API.
 *
 * Ketetapan Owner 24 Agustus 2026: di lapangan hanya ada Kurir — DISPATCHER pensiun (kekuasaan
 * menugaskan pekerjaan se-organisasi, alasannya tetap berlaku). EMPLOYEE dihidupkan kembali
 * 25 Agustus 2026 berlabel "Staff Admin" — lihat `ROLE_CHOICES` di bawah.
 *
 * TETAP ADA di `ROLE_CHOICES` di bawah, dan itu bukan kelalaian. Daftar itu melayani DUA hal —
 * pilihan penugasan DAN label. Mencabut entrinya membuat orang yang masih memegang peran ini
 * kehilangan namanya di layar (`roleLabel` jatuh ke kode mentah "DISPATCHER"), dan lebih buruk lagi
 * `rolesOf` akan mengembalikan daftar KOSONG untuknya — ia tampak tidak punya peran sama sekali,
 * yaitu tampilan yang persis dipakai untuk menandai "pekerja ini tidak dapat masuk".
 *
 * Jadi yang disaring adalah PILIHAN-nya (`assignableRoles`), bukan pengetahuan tentangnya.
 */
export const ROLE_PENSIUN: readonly WorkforceRole[] = ['DISPATCHER'];

/** Urutan pilihan di layar: yang paling sering dipakai lebih dulu, bukan urutan abjad. */
export const ROLE_CHOICES: readonly {
  readonly role: WorkforceRole;
  readonly label: string;
  readonly hint: string;
}[] = [
  {
    role: 'COURIER',
    label: 'Kurir',
    hint: 'Absen sendiri, menjalankan tugas dan pengiriman. Hanya melihat datanya sendiri.',
  },
  {
    // Dihidupkan kembali 25 Agustus 2026, label baru "Staff Admin" — lihat ROLE_PENSIUN.
    role: 'EMPLOYEE',
    label: 'Staff Admin',
    hint: 'Absen sendiri dan mengajukan koreksi. Hanya melihat datanya sendiri.',
  },
  {
    // PENSIUN (lihat ROLE_PENSIUN) — disimpan hanya agar penugasan lama tetap punya nama.
    role: 'DISPATCHER',
    label: 'Dispatcher',
    hint: 'Membuat dan menugaskan pekerjaan se-organisasi. Tidak menyetujui koreksi absensi.',
  },
  {
    role: 'MANAGER',
    label: 'Manajer',
    hint: 'Melihat dan menyetujui koreksi untuk timnya.',
  },
  {
    // Label tampilan saja, keputusan Owner 25 Agustus 2026 — tak ada peran "OWNER" terpisah di
    // enum database; ini tetaplah `WORKFORCE_ADMIN`, izin & akses tak berubah.
    role: 'WORKFORCE_ADMIN',
    label: 'Owner',
    hint: 'Mengelola pekerja, lokasi, dan kebijakan se-organisasi. Tidak mengeksekusi tugas.',
  },
];

/** Peran yang dimiliki satu orang, dalam urutan `ROLE_CHOICES` agar tampilannya stabil. */
export function rolesOf(
  assignments: readonly RoleAssignment[],
  userId: string,
): readonly WorkforceRole[] {
  const dimiliki = new Set(assignments.filter((a) => a.userId === userId).map((a) => a.role));
  return ROLE_CHOICES.filter((c) => dimiliki.has(c.role)).map((c) => c.role);
}

export function roleLabel(role: WorkforceRole): string {
  return ROLE_CHOICES.find((c) => c.role === role)?.label ?? role;
}

/**
 * Benar bila salah satu peran adalah pengawas (Owner/Manajer) — beranda dan navigasi berbeda
 * dari Kurir/Karyawan (item 5/6, review 12 Sep 2026).
 *
 * COURIER dan EMPLOYEE dikelompokkan bersama karena keduanya "Hanya melihat datanya sendiri"
 * (lihat `hint` di `ROLE_CHOICES`) — perbedaannya cuma jenis pekerjaan (lapangan vs kantor), bukan
 * cakupan pandang. WORKFORCE_ADMIN dan MANAGER dikelompokkan bersama karena keduanya melihat
 * ORANG LAIN (K2, review 12 Sep 2026) — bedanya cakupan (organisasi vs tim), bukan jenisnya.
 *
 * Seseorang boleh punya KEDUANYA sekaligus (mis. Manajer yang juga Kurir) — pengawas MENANG:
 * K2 mewajibkan beranda Owner tetap tampil bagi siapa pun yang punya peran pengawas, meski ia
 * juga punya peran lapangan. DISPATCHER (pensiun) sengaja tidak disebut di sini sama sekali —
 * lihat `ROLE_PENSIUN`.
 */
export function isOversightRole(roles: readonly WorkforceRole[]): boolean {
  return roles.includes('WORKFORCE_ADMIN') || roles.includes('MANAGER');
}

/**
 * Pekerja yang TIDAK DAPAT MASUK: ada di roster, tetapi tanpa satu pun peran.
 *
 * Ini bukan sekadar penyaringan untuk kenyamanan. Sebelum ada layar ini, keadaan tersebut tidak
 * terlihat di mana pun: orangnya tampak beres di daftar pekerja, dan kegagalannya baru muncul di
 * layar PONSELNYA — pada pagi hari onboarding, di depan orang banyak.
 *
 * Pekerja non-aktif sengaja IKUT dihitung. Ia memang tidak seharusnya masuk, tetapi alasannya
 * berbeda, dan menyembunyikannya di sini membuat "sudah lengkap" berarti dua hal yang berlainan.
 */
export function workersWithoutRole(
  workers: readonly WorkerProfile[],
  assignments: readonly RoleAssignment[],
): readonly WorkerProfile[] {
  const punyaPeran = new Set(assignments.map((a) => a.userId));
  return workers.filter((w) => !punyaPeran.has(w.userId));
}

/**
 * Peran yang MASIH bisa ditambahkan untuk satu orang.
 *
 * Peran yang sudah dimiliki dibuang dari pilihan. Menugaskan ulang peran yang sama memang aman —
 * server idempoten (`onConflictDoNothing`) — tetapi menawarkannya membuat admin mengira ada yang
 * berubah padahal tidak ada.
 */
export function assignableRoles(
  assignments: readonly RoleAssignment[],
  userId: string,
): readonly WorkforceRole[] {
  const dimiliki = new Set(rolesOf(assignments, userId));
  return ROLE_CHOICES.filter((c) => !dimiliki.has(c.role) && !ROLE_PENSIUN.includes(c.role)).map(
    (c) => c.role,
  );
}
